import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiBox, FiEye, FiGrid, FiLayers, FiPlus, FiSave, FiTable, FiTrash2 } from "react-icons/fi";
import ProfileGalleryPostActionIcon from "../Content/ProfileGalleryPostActionIcon";
import "./AdminVisualDesignLibrary.css";

const STORAGE_KEY = "buzzvibe-admin-ux-library-v1";

const buttonSamples = [
  { id: "micro", label: "Micro", className: "admin-ux-button--micro" },
  { id: "mobile", label: "Mobile", className: "admin-ux-button--mobile" },
  { id: "small", label: "Small", className: "admin-ux-button--small" },
  { id: "medium", label: "Medium", className: "admin-ux-button--medium" },
  { id: "large", label: "Large", className: "admin-ux-button--large" },
];

const avatarScaleSamples = [
  { id: "micro-tiny", label: "Micro-tiny", width: "100px", avatarSize: "16px", fontSize: "0.56rem", gap: "4px", name: "Dubs The Creator" },
  { id: "micro", label: "Micro", width: "140px", avatarSize: "20px", fontSize: "0.64rem", gap: "5px", name: "Dubs The Creator" },
  { id: "small", label: "Small", width: "180px", avatarSize: "26px", fontSize: "0.74rem", gap: "7px", name: "Dubs The Creator" },
  { id: "mobile", label: "Mobile", width: "220px", avatarSize: "32px", fontSize: "0.84rem", gap: "8px", name: "Dubs The Creator" },
  { id: "tablet", label: "Tablet", width: "280px", avatarSize: "38px", fontSize: "0.94rem", gap: "10px", name: "Dubs The Creator" },
  { id: "desktop", label: "Desktop", width: "340px", avatarSize: "46px", fontSize: "1.04rem", gap: "12px", name: "Dubs The Creator" },
  { id: "large", label: "Large display", width: "420px", avatarSize: "56px", fontSize: "1.16rem", gap: "14px", name: "Dubs The Creator" },
];

const widgetSamples = [
  {
    id: "notification-row",
    title: "Notification Row",
    meta: "Avatar, message, action, status",
    body: "Dubs The Creator liked your new post.",
  },
  {
    id: "profile-stat",
    title: "Profile Stat",
    meta: "Compact counter module",
    body: "1,248 followers",
  },
  {
    id: "post-header",
    title: "Post Header",
    meta: "Avatar, name, date flow",
    body: "May 14th, 2026 / Edited June 20th, 2026",
  },
  {
    id: "shop-tab",
    title: "Scrollable Tab Rail",
    meta: "Profile and shop parity",
    body: "Posts / Products / Images / Sales / About",
  },
];

const engagementIconSamples = [
  { id: "like", label: "Like", type: "like" },
  { id: "comment", label: "Comment", type: "comment" },
  { id: "share", label: "Share", type: "share" },
];

const libraryTabs = [
  { id: "objects", label: "Objects", icon: FiBox },
  { id: "buttons", label: "Buttons", icon: FiGrid },
  { id: "widgets", label: "Widgets", icon: FiLayers },
  { id: "forms-tables", label: "Forms & Tables", icon: FiTable },
];

const eventCards = [
  { id: "compact", label: "Compact Bento", title: "Laguna Sunrise", details: "Small rail card" },
  { id: "feature", label: "Featured Bento", title: "GameBird Live Event", details: "Large editorial card" },
  { id: "list", label: "List Bento", title: "Hollywood Pop-Up", details: "Dense scan card" },
];

const createBlankCustomItem = () => ({
  id: `custom-${Date.now()}`,
  name: "New element",
  type: "Button",
  size: "Medium",
  notes: "Describe the pattern, placement, and allowed variants.",
});

const loadSavedDocument = () => {
  try {
    const savedDocument = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      themeNotes: savedDocument.themeNotes || "Live white label theme notes, decisions, and widget changes.",
      customItems: Array.isArray(savedDocument.customItems) ? savedDocument.customItems : [],
    };
  } catch (error) {
    return {
      themeNotes: "Live white label theme notes, decisions, and widget changes.",
      customItems: [],
    };
  }
};

const AdminVisualDesignLibrary = () => {
  const [activeLibraryTab, setActiveLibraryTab] = useState("objects");
  const [themeNotes, setThemeNotes] = useState(() => loadSavedDocument().themeNotes);
  const [customItems, setCustomItems] = useState(() => loadSavedDocument().customItems);
  const [draftItem, setDraftItem] = useState(() => createBlankCustomItem());

  const savedDocument = useMemo(() => ({ themeNotes, customItems }), [themeNotes, customItems]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedDocument));
  }, [savedDocument]);

  const addCustomItem = (event) => {
    event.preventDefault();
    setCustomItems((items) => [...items, { ...draftItem, id: `custom-${Date.now()}` }]);
    setDraftItem(createBlankCustomItem());
  };

  const updateCustomItem = (id, field, value) => {
    setCustomItems((items) => items.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  };

  const removeCustomItem = (id) => {
    setCustomItems((items) => items.filter((item) => item.id !== id));
  };

  return (
    <main className="admin-ux-library">
      <header className="admin-ux-hero">
        <div>
          <p className="admin-ux-eyebrow">Live White Label Theme</p>
          <h1>UX Visual Design Library</h1>
          <p className="admin-ux-hero-copy">Admin-only working document for reusable elements, widget patterns, and theme decisions.</p>
        </div>
        <div className="admin-ux-hero-actions">
          <Link to="/admin/theme" className="admin-ux-link-button">
            <FiEye aria-hidden="true" />
            Theme Manager
          </Link>
          <Link to="/admin-ads" className="admin-ux-link-button admin-ux-link-button-secondary">
            <FiEye aria-hidden="true" />
            AD Center
          </Link>
        </div>
      </header>

      <nav className="admin-ux-library-tabs" aria-label="UX library pattern groups">
        {libraryTabs.map((tab) => {
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              className={`admin-ux-library-tab ${activeLibraryTab === tab.id ? "admin-ux-library-tab--active" : ""}`}
              onClick={() => setActiveLibraryTab(tab.id)}
            >
              <Icon aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {activeLibraryTab === "forms-tables" && (
        <section className="admin-ux-section admin-ux-forms-tables-section">
          <div className="admin-ux-section-heading">
            <h2>Forms & Tables</h2>
            <span>Admin/profile production model</span>
          </div>
          <div className="admin-ux-form-table-grid">
            <form className="admin-ux-form-sample">
              <label>
                <span>Field Label</span>
                <input value="Readable field value" readOnly />
              </label>
              <label>
                <span>Mode</span>
                <select value="active" onChange={() => {}}>
                  <option value="active">Active</option>
                </select>
              </label>
              <label className="admin-ux-form-sample-wide">
                <span>Notes</span>
                <textarea value="Use compact labels, 8px radius fields, strong focus rings, and clean grouped actions." readOnly rows={4} />
              </label>
              <button type="button" className="admin-ux-button admin-ux-button--medium">
                <FiSave aria-hidden="true" />
                Save Pattern
              </button>
            </form>

            <div className="admin-ux-table-shell">
              <table className="admin-ux-table-sample">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Theme token</td>
                    <td><span className="admin-ux-status-pill">Active</span></td>
                    <td>Form</td>
                    <td>Today</td>
                    <td><button type="button" className="admin-ux-icon-action"><FiEye aria-hidden="true" />View</button></td>
                  </tr>
                  <tr>
                    <td>Responsive table</td>
                    <td><span className="admin-ux-status-pill admin-ux-status-pill--review">Review</span></td>
                    <td>Table</td>
                    <td>Today</td>
                    <td><button type="button" className="admin-ux-icon-action"><FiSave aria-hidden="true" />Use</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {activeLibraryTab === "buttons" && (
        <section className="admin-ux-section">
        <div className="admin-ux-section-heading">
          <h2>Button Scale</h2>
          <span>Micro / Mobile / Small / Medium / Large</span>
        </div>
        <div className="admin-ux-button-grid">
          {buttonSamples.map((button) => (
            <div key={button.id} className="admin-ux-pattern-tile">
              <button type="button" className={`admin-ux-button ${button.className}`}>
                <FiSave aria-hidden="true" />
                {button.label}
              </button>
              <p>{button.label} button</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {activeLibraryTab === "widgets" && (
        <>
      <section className="admin-ux-section">
        <div className="admin-ux-section-heading">
          <h2>Engagement Icons</h2>
          <span>Standard post action set</span>
        </div>
        <div className="admin-ux-engagement-icon-set" aria-label="MATRIX post engagement icons">
          {engagementIconSamples.map((icon) => (
            <span key={icon.id} className="admin-ux-engagement-icon-button">
              <ProfileGalleryPostActionIcon type={icon.type} active={icon.id === "like"} />
              <span>{icon.label}</span>
            </span>
          ))}
        </div>
      </section>

      <section className="admin-ux-section">
        <div className="admin-ux-section-heading">
          <h2>Widget Treatments</h2>
          <span>Reusable surface patterns</span>
        </div>
        <div className="admin-ux-widget-grid">
          {widgetSamples.map((widget) => (
            <article key={widget.id} className="admin-ux-widget-card">
              <span>{widget.meta}</span>
              <h3>{widget.title}</h3>
              <p>{widget.body}</p>
            </article>
          ))}
        </div>
      </section>
      </>
      )}

      {activeLibraryTab === "objects" && (
        <>
      <section className="admin-ux-section">
        <div className="admin-ux-section-heading">
          <h2>Avatar Pill Scale</h2>
          <span>100px micro to large display</span>
        </div>
        <div className="admin-ux-avatar-scale-grid">
          {avatarScaleSamples.map((sample) => (
            <article key={sample.id} className="admin-ux-avatar-scale-card">
              <span>{sample.label}</span>
              <div className="admin-ux-avatar-stage" style={{ "--admin-ux-avatar-pill-width": sample.width }}>
                <div
                  className="admin-ux-avatar-pill"
                  style={{
                    "--admin-ux-avatar-pill-width": sample.width,
                    "--admin-ux-avatar-size": sample.avatarSize,
                    "--admin-ux-avatar-name-size": sample.fontSize,
                    "--admin-ux-avatar-gap": sample.gap,
                  }}
                >
                  <img src="/logo512.png" alt="Avatar sample" />
                  <strong className="admin-ux-avatar-pill-name">{sample.name}</strong>
                </div>
              </div>
              <p>{sample.width} container</p>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-ux-section">
        <div className="admin-ux-section-heading">
          <h2>Event Bento Cards</h2>
          <span>Side-by-side comparison</span>
        </div>
        <div className="admin-ux-event-comparison">
          {eventCards.map((eventCard) => (
            <article key={eventCard.id} className={`admin-ux-event-card admin-ux-event-card--${eventCard.id}`}>
              <div className="admin-ux-event-card-media">
                <img src="/logo512.png" alt="MATRIX preview" />
              </div>
              <div className="admin-ux-event-card-body">
                <span>{eventCard.label}</span>
                <h3>{eventCard.title}</h3>
                <p>{eventCard.details}</p>
                <button type="button" className="admin-ux-button admin-ux-button--small">Going</button>
              </div>
            </article>
          ))}
        </div>
      </section>
      </>
      )}

      <section className="admin-ux-section admin-ux-document-section">
        <div className="admin-ux-section-heading">
          <h2>Working Document</h2>
          <span>Saved in this browser</span>
        </div>
        <label className="admin-ux-field">
          <span>Theme Notes</span>
          <textarea value={themeNotes} onChange={(event) => setThemeNotes(event.target.value)} rows={5} />
        </label>

        <form className="admin-ux-create-row" onSubmit={addCustomItem}>
          <label>
            <span>Name</span>
            <input value={draftItem.name} onChange={(event) => setDraftItem({ ...draftItem, name: event.target.value })} />
          </label>
          <label>
            <span>Type</span>
            <input value={draftItem.type} onChange={(event) => setDraftItem({ ...draftItem, type: event.target.value })} />
          </label>
          <label>
            <span>Size</span>
            <input value={draftItem.size} onChange={(event) => setDraftItem({ ...draftItem, size: event.target.value })} />
          </label>
          <button type="submit" className="admin-ux-button admin-ux-button--medium">
            <FiPlus aria-hidden="true" />
            Add Pattern
          </button>
        </form>

        <div className="admin-ux-custom-list">
          {customItems.map((item) => (
            <article key={item.id} className="admin-ux-custom-item">
              <div className="admin-ux-custom-fields">
                <input value={item.name} onChange={(event) => updateCustomItem(item.id, "name", event.target.value)} aria-label="Pattern name" />
                <input value={item.type} onChange={(event) => updateCustomItem(item.id, "type", event.target.value)} aria-label="Pattern type" />
                <input value={item.size} onChange={(event) => updateCustomItem(item.id, "size", event.target.value)} aria-label="Pattern size" />
              </div>
              <textarea value={item.notes} onChange={(event) => updateCustomItem(item.id, "notes", event.target.value)} rows={3} aria-label="Pattern notes" />
              <button type="button" className="admin-ux-delete-button" onClick={() => removeCustomItem(item.id)}>
                <FiTrash2 aria-hidden="true" />
                Remove
              </button>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default AdminVisualDesignLibrary;